import React, { useState, useEffect } from "react";
import UploadWizard     from "./components/UploadWizard";
import PipelineStatus   from "./components/PipelineStatus";
import ResultsDashboard from "./components/ResultsDashboard";
import alphansoLogo     from "./assets/alphansologo.png";

const BENEFICIARY_TYPES = [
  { value: "",           label: "All Beneficiaries",            icon: "👥" },
  { value: "children",   label: "Children (0–5 yrs)",           icon: "👶" },
  { value: "pregnant",   label: "Pregnant Women",               icon: "🤰" },
  { value: "lactating",  label: "Lactating Mothers",            icon: "🍼" },
  { value: "adolescent", label: "Adolescent Girls (14–18 yrs)", icon: "🎓" },
];

export default function App() {
  const [showInfoModal,  setShowInfoModal]  = useState(false);
  const [step,           setStep]           = useState("upload");
  const [uploadedFile,   setUploadedFile]   = useState(null);
  const [result,         setResult]         = useState(null);
  const [error,          setError]          = useState(null);
  const [uploadCount,    setUploadCount]    = useState(0);
  const [filterDistrict, setFilterDistrict] = useState("");
  const [filterType,     setFilterType]     = useState("");

  function handleUploadStart(file) {
    setUploadedFile(file);
    setResult(null);
    setError(null);
    setFilterDistrict("");
    setFilterType("");
    setUploadCount(c => c + 1);
    setStep("processing");
  }

  function handleProcessingDone(data) {
    setFilterDistrict("");
    setFilterType("");
    setResult(data);
    setStep("results");
  }

  function handleError(msg) {
    setError(msg);
    setStep("upload");
  }

  function handleReset() {
    setStep("upload");
    setResult(null);
    setError(null);
    setUploadedFile(null);
    setFilterDistrict("");
    setFilterType("");
  }

  const showRight    = step === "processing" || step === "results";
  const isProcessing = step === "processing";
  const predictions  = result?.predictions || [];
  const districts    = [...new Set(predictions.map(p => p.district).filter(Boolean))].sort();

  return (
    <div style={s.root}>
      {showInfoModal && <PoshanInfoModal onClose={() => setShowInfoModal(false)} />}
      <Header onInfo={() => setShowInfoModal(true)} />
      <div style={s.body}>


        {/* ── LEFT PANEL ── */}
        <div style={{ ...s.left, maxWidth: showRight ? 300 : 500 }}>
          <StepBar step={step} />

          <div style={s.leftInner}>
            {isProcessing && (
              <div style={s.overlay}>
                <div style={s.overlayBox}>
                  <div style={{ fontSize: 36 }}>⏳</div>
                  <div style={s.overlayTitle}>Processing…</div>
                  <div style={s.overlaySub}>Please wait</div>
                </div>
              </div>
            )}

            <div style={{
              ...s.scroll,
              opacity: isProcessing ? 0.4 : 1,
              pointerEvents: isProcessing ? "none" : "auto",
            }}>
              <UploadWizard
                onUploadStart={handleUploadStart}
                error={error}
                compact={showRight}
              />

              {/* ── FILTER PANEL — results step only ── */}
              {step === "results" && (
                <FilterPanel
                  districts={districts}
                  filterDistrict={filterDistrict}
                  filterType={filterType}
                  onDistrict={setFilterDistrict}
                  onType={setFilterType}
                />
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        {showRight && (
          <div style={s.right}>
            {step === "processing" && (
              <PipelineStatus
                key={uploadCount}
                file={uploadedFile}
                onDone={handleProcessingDone}
                onError={handleError}
              />
            )}
            {step === "results" && result && (
              <ResultsDashboard
                key={uploadCount}
                result={result}
                onReset={handleReset}
                filterDistrict={filterDistrict}
                filterType={filterType}
              />
            )}
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
}

/* ── Filter Panel ────────────────────────────────────────────────────────── */
function FilterPanel({ districts, filterDistrict, filterType, onDistrict, onType }) {
  const isFiltered = filterDistrict !== "" || filterType !== "";
  return (
    <div style={s.filterPanel}>
      <div style={s.filterPanelTitle}>🔍 Filters</div>

      {/* District */}
      <div style={s.filterGroup}>
        <label style={s.filterLabel}>🗺️ Cities / Districts</label>
        <select
          value={filterDistrict}
          onChange={e => onDistrict(e.target.value)}
          style={s.filterSelect}
        >
          <option value="">All Cities / Districts</option>
          {districts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {/* Beneficiary Type */}
      <div style={s.filterGroup}>
        <label style={s.filterLabel}>👥 Beneficiary Type</label>
        <select
          value={filterType}
          onChange={e => onType(e.target.value)}
          style={s.filterSelect}
        >
          {BENEFICIARY_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
          ))}
        </select>
      </div>

      {isFiltered && (
        <button
          style={s.clearBtn}
          onClick={() => { onDistrict(""); onType(""); }}
        >
          ✕ Clear Filters
        </button>
      )}
    </div>
  );
}

/* ── Header ──────────────────────────────────────────────────────────────── */
function Header({ onInfo }) {
  return (
    <header style={s.header}>
      {/* Left — Logo + Title */}
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <img src={alphansoLogo} alt="Alphaso Technology" style={s.headerLogo} />
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <div style={{ color: "#bfdbfe", fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase" }}>
            Alphaso Technology
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#fff", fontSize: 17, fontWeight: 700, letterSpacing: 0.3 }}>
              Poshan Tracker AI Analytics
            </span>
          </div>
        </div>
      </div>

      {/* Right — Badges + Button */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={s.badge1}>AI Powered</span>
        <span style={s.badge2}>40L+ Beneficiaries</span>
        <button style={s.infoBtn} onClick={onInfo}>
          📊 Analytics Guide
        </button>
      </div>
    </header>
  );
}

/* ── Footer ──────────────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer style={s.footer}>
      <div style={s.footerInner}>
        <img src={alphansoLogo} alt="Alphaso Technology" style={s.footerLogo} />
        <div style={{ color: "#6b7280", fontSize: 12 }}>
          © {new Date().getFullYear()} Alphaso Technology. All rights reserved.
        </div>
        <div style={{ color: "#9ca3af", fontSize: 11 }}>
          Poshan Tracker AI Analytics · Gujarat WCD POC · v1.0
        </div>
      </div>
    </footer>
  );
}

/* ── Step Bar ────────────────────────────────────────────────────────────── */
function StepBar({ step }) {
  const steps = [
    { key: "upload",     label: "Upload",     icon: "📄" },
    { key: "processing", label: "Processing", icon: "⚙️" },
    { key: "results",    label: "Results",    icon: "📊" },
  ];
  const idx = steps.findIndex(st => st.key === step);
  return (
    <div style={s.stepBar}>
      {steps.map((st, i) => {
        const done = i < idx, active = i === idx;
        return (
          <React.Fragment key={st.key}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{
                width: 34, height: 34, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, fontWeight: 700, transition: "all 0.3s",
                background: done ? "#057a55" : active ? "#1a56db" : "#e5e7eb",
                color: (done || active) ? "#fff" : "#9ca3af",
                boxShadow: active ? "0 0 0 4px #bfdbfe" : "none",
              }}>
                {done ? "✓" : st.icon}
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: active ? "#1a56db" : done ? "#057a55" : "#9ca3af" }}>
                {st.label}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, borderRadius: 2, marginBottom: 18, minWidth: 24, maxWidth: 60, background: done ? "#057a55" : "#e5e7eb" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────────────────── */
const s = {
  root:           { minHeight: "100vh", background: "#f1f5f9", fontFamily: "'Segoe UI',Arial,sans-serif", display: "flex", flexDirection: "column" },
  header:         { background: "linear-gradient(135deg,#1e3a8a,#1a56db)", padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 2px 8px rgba(0,0,0,.2)", flexShrink: 0 },
  headerLogo:     { height: 42, width: "auto", objectFit: "contain", flexShrink: 0 },
  footerLogo:     { height: 28, width: "auto", objectFit: "contain" },
  footer:         { background: "#fff", borderTop: "1px solid #e5e7eb", flexShrink: 0 },
  footerInner:    { maxWidth: 1200, margin: "0 auto", padding: "12px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 },
  badge1:         { background: "#1d4ed8", color: "#bfdbfe", border: "1px solid #3b82f6", borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 600 },
  badge2:         { background: "#065f46", color: "#6ee7b7", border: "1px solid #059669", borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 600 },
  infoBtn:        { background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", backdropFilter: "blur(4px)" },
  body:           { display: "flex", flex: 1, overflow: "hidden" },
  left:           { display: "flex", flexDirection: "column", background: "#fff", borderRight: "1px solid #e5e7eb", flexShrink: 0, transition: "max-width 0.3s ease", minWidth: 280 },
  stepBar:        { display: "flex", alignItems: "center", justifyContent: "center", padding: "14px 16px", borderBottom: "1px solid #f1f5f9", gap: 0 },
  leftInner:      { position: "relative", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
  scroll:         { flex: 1, overflowY: "auto", padding: "20px 16px", transition: "opacity 0.3s", display: "flex", flexDirection: "column", gap: 20 },
  overlay:        { position: "absolute", inset: 0, zIndex: 10, background: "rgba(255,255,255,.7)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(2px)" },
  overlayBox:     { background: "#fff", borderRadius: 14, padding: "28px 32px", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,.12)", border: "1px solid #e5e7eb" },
  overlayTitle:   { fontWeight: 700, fontSize: 16, color: "#111827", marginTop: 10 },
  overlaySub:     { fontSize: 13, color: "#6b7280", marginTop: 4 },
  right:          { flex: 1, overflowY: "auto", padding: "24px 28px", background: "#f1f5f9" },
  filterPanel:    { background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 14px 12px", display: "flex", flexDirection: "column", gap: 14 },
  filterPanelTitle:{ fontSize: 13, fontWeight: 700, color: "#1e3a8a", borderBottom: "1px solid #e5e7eb", paddingBottom: 8 },
  filterGroup:    { display: "flex", flexDirection: "column", gap: 6 },
  filterLabel:    { fontSize: 12, fontWeight: 600, color: "#374151" },
  filterSelect:   { width: "100%", padding: "7px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, color: "#111827", background: "#fff", cursor: "pointer", outline: "none" },
  clearBtn:       { padding: "6px 12px", borderRadius: 8, border: "1px solid #fca5a5", background: "#fef2f2", color: "#dc2626", fontSize: 12, fontWeight: 600, cursor: "pointer", alignSelf: "flex-start" },
};


/* ── Poshan Info Modal ───────────────────────────────────────────────────── */
function PoshanInfoModal({ onClose }) {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    fetch("/api/config")
      .then(r => r.json())
      .then(data => setCfg(data))
      .catch(() => setCfg({}));
  }, []);

  const DISTRICTS          = cfg?.districts           || [];
  const BENEFICIARY_TARGETS = cfg?.beneficiary_targets || [];
  const RISK_RULES          = cfg?.risk_rules          || [];
  const ALERT_RULES         = cfg?.alert_display_rules || [];

  return (
    <div style={m.overlay} onClick={onClose}>
      <div style={m.modal} onClick={e => e.stopPropagation()}>
        {!cfg && (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280", fontSize: 14 }}>
            ⏳ Loading…
          </div>
        )}
        {cfg && (
          <>
            {/* Header */}
            <div style={m.header}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{ fontSize: 36 }}>📊</span>
                <div>
                  <div style={m.title}>Poshan Tracker — Analytics Reference Guide</div>
                  <div style={m.subtitle}>Static rule-based analysis · WHO standards · Gujarat WCD POC</div>
                </div>
              </div>
              <button style={m.closeBtn} onClick={onClose}>✕</button>
            </div>

            <div style={m.body}>
              {/* Districts */}
              <div style={m.section}>
                <div style={m.sectionTitle}>🗺️ POC Districts Covered</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {DISTRICTS.map(d => (
                    <span key={d} style={m.districtChip}>{d}</span>
                  ))}
                </div>
              </div>

              {/* Beneficiary Targets */}
              <div style={m.section}>
                <div style={m.sectionTitle}>👥 Beneficiary Categories & Targets</div>
                <div style={m.grid2}>
                  {BENEFICIARY_TARGETS.map(b => (
                    <div key={b.type} style={{ ...m.card, background: b.bg, borderLeft: `4px solid ${b.color}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <span style={{ fontSize: 24 }}>{b.icon}</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>{b.type}</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: b.color }}>{b.target}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{b.note}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Risk Rules */}
              <div style={m.section}>
                <div style={m.sectionTitle}>⚙️ Rule-Based Risk Scoring (WHO Standards)</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {RISK_RULES.map(r => (
                    <div key={r.label} style={{ ...m.ruleCard, borderLeft: `4px solid ${r.color}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                        <span style={{ fontSize: 20 }}>{r.icon}</span>
                        <span style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>{r.label}</span>
                      </div>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={m.ruleTh}>Risk Level & Score</th>
                            <th style={m.ruleTh}>Trigger Condition</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.rules.map((rule, i) => (
                            <tr key={i} style={{ background: i % 2 === 0 ? "#f9fafb" : "#fff" }}>
                              <td style={{ ...m.ruleTd, fontWeight: 700, color: i === 0 ? "#dc2626" : i === 1 ? "#d97706" : "#057a55" }}>{rule.level}</td>
                              <td style={{ ...m.ruleTd, fontFamily: "monospace", fontSize: 12 }}>{rule.trigger}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div style={m.ruleNote}>ℹ️ {r.note}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Alert Rules */}
              <div style={m.section}>
                <div style={m.sectionTitle}>🔔 DSS Alert Trigger Rules</div>
                <div style={m.grid2}>
                  {ALERT_RULES.map(a => (
                    <div key={a.label} style={{ ...m.alertCard, background: a.bg }}>
                      <div style={{ fontSize: 22, marginBottom: 6 }}>{a.icon}</div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: a.severity, marginBottom: 4 }}>{a.label}</div>
                      <div style={{ fontSize: 12, color: "#374151" }}>{a.rule}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Overall Risk formula */}
              <div style={{ ...m.section, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#057a55", marginBottom: 8 }}>📐 Overall Risk Formula</div>
                <div style={{ fontFamily: "monospace", fontSize: 13, color: "#065f46", background: "#dcfce7", borderRadius: 8, padding: "10px 14px", marginBottom: 8 }}>
                  Overall Risk = max(malnutrition_risk, anemia_risk, growth_risk)
                </div>
                <div style={{ fontSize: 12, color: "#374151" }}>
                  The highest individual risk score determines the overall risk level per beneficiary. A beneficiary needs attention if <strong>any single risk</strong> breaches the threshold — not just the average.
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const m = {
  overlay:     { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 },
  modal:       { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 820, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.28)" },
  header:      { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "20px 24px 16px", background: "linear-gradient(135deg,#1e3a8a,#1a56db)", borderRadius: "16px 16px 0 0" },
  title:       { fontSize: 18, fontWeight: 800, color: "#fff" },
  subtitle:    { fontSize: 13, color: "#bfdbfe", marginTop: 3 },
  closeBtn:    { background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 8, cursor: "pointer", fontSize: 16, color: "#fff", padding: "6px 12px", fontWeight: 700 },
  body:        { padding: "20px 24px 28px", display: "flex", flexDirection: "column", gap: 24 },
  section:     {},
  sectionTitle:{ fontWeight: 700, fontSize: 14, color: "#1e3a8a", marginBottom: 12, borderBottom: "2px solid #eff6ff", paddingBottom: 6 },
  grid2:       { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 },
  card:        { border: "1px solid #e5e7eb", borderRadius: 10, padding: "14px 16px" },
  districtChip:{ background: "#eff6ff", color: "#1a56db", border: "1px solid #bfdbfe", borderRadius: 20, padding: "5px 14px", fontSize: 13, fontWeight: 600 },
  ruleCard:    { background: "#fafafa", border: "1px solid #e5e7eb", borderRadius: 10, padding: "14px 16px" },
  ruleTh:      { background: "#1a56db", color: "#fff", padding: "8px 12px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  ruleTd:      { padding: "7px 12px", borderBottom: "1px solid #f3f4f6" },
  ruleNote:    { fontSize: 11, color: "#6b7280", marginTop: 10, lineHeight: 1.5, fontStyle: "italic" },
  alertCard:   { border: "1px solid #e5e7eb", borderRadius: 10, padding: "14px 16px" },
};
